import {
  type CreateReference,
  createCodeableConcept,
  createReference,
  createReferenceFromResource,
  type DocumentTypeCode,
  DocumentTypeCodeDescription,
  type Editor,
  getFirstCodeFromCodableConcepts,
  system,
} from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import type { Attachment, DocumentReference } from 'fhir/r4';

import { type Document, type DocumentCreate, DocumentSchema } from './schema';

export const mapDocumentCreateModelToFhir = (
  document: DocumentCreate,
  attachment: Attachment,
  admission: CreateReference,
  author: Editor,
): DocumentReference => {
  const { type, patient } = document;
  const DocumentTypeDisplay = DocumentTypeCodeDescription[type];

  const documentReference: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    type: createCodeableConcept(
      type,
      DocumentTypeDisplay,
      DocumentTypeDisplay,
      system,
    ),
    date: new Date().toISOString(),
    context: {
      encounter: [admission],
    },
    subject: createReference('Patient', patient),
    content: [
      {
        attachment,
      },
    ],
    author: [createReferenceFromResource(author)],
  };

  return documentReference;
};

export const mapDocumentReferenceToModel = (
  documentReference: DocumentReference,
): Document => {
  const { content, status, author, date, subject, context, type, id } =
    documentReference;
  const documentTypeCode = type
    ? (getFirstCodeFromCodableConcepts([type]) as DocumentTypeCode)
    : undefined;

  const firstContent = first(content);
  const document = {
    id,
    patient: subject,
    author: first(author),
    created: date,
    status: status,
    document: firstContent
      ? {
          contentType: firstContent.attachment?.contentType,
          key: firstContent.attachment?.url,
          title: firstContent.attachment?.title,
        }
      : undefined,
    admission: first(context?.encounter),
    type: documentTypeCode,
  };

  const parsedResult = DocumentSchema.safeParse(document);

  if (parsedResult.error) {
    throw new Error(JSON.stringify(parsedResult.error.errors));
  }

  return parsedResult.data;
};
